import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './SidebarGroup.css';
import { preloadRoute } from '../utils/routePreload';

const renderIcon = (icon, className) => {
  if (!icon) return null;
  if (typeof icon === 'string') return <span className={className}>{icon}</span>;
  const Icon = icon;
  return (
    <span className={className} aria-hidden="true">
      <Icon size={17} strokeWidth={1.9} />
    </span>
  );
};

function SidebarGroup({
  domain,
  icon,
  isCollapsed,
  expanded,
  onToggle,
  items,
  isVisible,
  location,
  onNavigate
}) {
  const [showFlyout, setShowFlyout] = useState(false);
  const [expandedItems, setExpandedItems] = useState(() => ({}));

  const filterTree = (navItems) => navItems
    .map(item => {
      const children = item.children ? filterTree(item.children) : undefined;
      return { ...item, children };
    })
    .filter(item => isVisible(item) && (!item.children || item.children.length > 0 || item.path));

  const visibleItems = filterTree(items);
  const isPathActive = (path, includeHash = false) => {
    if (!path) return false;
    const [pathname, hash] = path.split('#');
    if (location.pathname !== pathname) return false;
    if (!includeHash) return true;
    return hash ? location.hash === `#${hash}` : !location.hash;
  };

  const hasActiveChild = (item) => item.children?.some(child => isPathActive(child.path, true) || hasActiveChild(child));
  const itemId = (item, fallback = '') => item.path || `${item.label}-${fallback}`;
  const isExpandedItem = (item, fallback) => {
    const key = itemId(item, fallback);
    return expandedItems[key] ?? (isPathActive(item.path) || hasActiveChild(item)) ?? false;
  };
  const toggleItem = (item, fallback) => {
    const key = itemId(item, fallback);
    setExpandedItems(prev => ({ ...prev, [key]: !(prev[key] ?? false) }));
  };

  const renderNestedItems = (nestedItems, depth = 0) => (
    <ul className={depth === 0 ? 'submenu-children' : 'submenu-children nested'}>
      {nestedItems.map((child, childIdx) => {
        const hasChildren = !!child.children?.length;
        const childExpanded = hasChildren && isExpandedItem(child, `${depth}-${childIdx}`);
        const childActive = isPathActive(child.path, true) || hasActiveChild(child);
        return (
          <li key={itemId(child, `${depth}-${childIdx}`)} className="submenu-child-item">
            <div className="submenu-child-row">
              {child.path && !hasChildren ? (
                <NavLink
                  to={child.path}
                  onClick={onNavigate}
                  onMouseEnter={() => preloadRoute(child.path)}
                  onFocus={() => preloadRoute(child.path)}
                  className={() => `submenu-child-link ${childActive ? 'active' : ''}`}
                >
                  {renderIcon(child.icon, 'submenu-child-icon')}
                  <span className="submenu-label">{child.label}</span>
                </NavLink>
              ) : (
                <button
                  type="button"
                  className={`submenu-child-link submenu-child-button ${childActive ? 'active' : ''}`}
                  aria-expanded={childExpanded}
                  onClick={() => toggleItem(child, `${depth}-${childIdx}`)}
                >
                  {renderIcon(child.icon, 'submenu-child-icon')}
                  <span className="submenu-label">{child.label}</span>
                  <span className="submenu-nested-toggle">
                    {childExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </button>
              )}
            </div>
            {hasChildren && childExpanded && renderNestedItems(child.children, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      className="sidebar-domain"
      onMouseEnter={() => isCollapsed && setShowFlyout(true)}
      onMouseLeave={() => isCollapsed && setShowFlyout(false)}
    >
      <button
        className={`domain-header ${expanded ? 'expanded' : 'collapsed'}`}
        onClick={onToggle}
        title={isCollapsed ? domain : ''}
      >
        {renderIcon(icon, 'domain-icon')}
        {!isCollapsed && (
          <>
            <span className="domain-name">{domain}</span>
            <span className="domain-toggle">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          </>
        )}
      </button>

      {/* Expanded submenu (visible when not collapsed and expanded) */}
      {expanded && !isCollapsed && (
        <ul className="domain-submenu expanded-mode">
          {visibleItems.map((item, idx) => {
            const hasChildren = !!item.children?.length;
            const itemExpanded = hasChildren && isExpandedItem(item, `${idx}`);
            const itemActive = isPathActive(item.path) || hasActiveChild(item);
            return (
              <li key={itemId(item, `${idx}`)} className={`submenu-item ${hasChildren ? 'has-children' : ''}`}>
                <div className="submenu-row">
                  <NavLink
                    to={item.path}
                    onClick={onNavigate}
                    onMouseEnter={() => preloadRoute(item.path)}
                    onFocus={() => preloadRoute(item.path)}
                    className={() => `submenu-link ${itemActive ? 'active' : ''} ${hasChildren ? 'with-toggle' : ''}`}
                  >
                    {renderIcon(item.icon, 'submenu-icon')}
                    <span className="submenu-label">{item.label}</span>
                  </NavLink>
                  {hasChildren && (
                    <button
                      type="button"
                      className="submenu-expand"
                      aria-label={`${itemExpanded ? 'Collapse' : 'Expand'} ${item.label}`}
                      aria-expanded={itemExpanded}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleItem(item, `${idx}`);
                      }}
                    >
                      {itemExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  )}
                </div>
                {itemExpanded && renderNestedItems(item.children)}
              </li>
            );
          })}
        </ul>
      )}

      {/* Flyout menu (visible when collapsed and hovering) */}
      {isCollapsed && showFlyout && visibleItems.length > 0 && (
        <div className="domain-flyout">
          <div className="flyout-header">{domain}</div>
          <ul className="flyout-menu">
            {visibleItems.map((item, idx) => (
              <li key={idx} className="flyout-item">
                <NavLink
                  to={item.path}
                  onMouseEnter={() => preloadRoute(item.path)}
                  onFocus={() => preloadRoute(item.path)}
                  className={() => `flyout-link ${isPathActive(item.path) || hasActiveChild(item) ? 'active' : ''}`}
                  onClick={() => {
                    setShowFlyout(false);
                    onNavigate?.();
                  }}
                >
                  {renderIcon(item.icon, 'flyout-icon')}
                  <span className="flyout-label">{item.label}</span>
                </NavLink>
                {item.children?.length > 0 && renderNestedItems(item.children)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SidebarGroup;
